import { useMutation, useQuery } from '@apollo/react-hooks';
import { getString, keyExists } from '@nackle/intl-tools';
import { useEffect, useMemo, useState } from 'react';
import { PATCH_PREFERRED_LANGUAGE } from '../../gql/mutations/PATCH_PREFERRED_LANGUAGE';
import { LOCALES_QUERY } from '../../gql/queries/LOCALES_QUERY';
import { PREFERRED_LANGUAGE_QUERY } from '../../gql/queries/PREFERRED_LANGUAGE_QUERY';
import { translationService } from '../../services';
import { useAuthState } from '../auth';

export const useTranslations = (  ) => {

    const { claims } = useAuthState();

    const { data: preferredLanguageData } = useQuery( PREFERRED_LANGUAGE_QUERY, { fetchPolicy: 'network-only' } );

    const [ patchLanguage ] = useMutation( PATCH_PREFERRED_LANGUAGE );

    const [ isLoading, setLoading ] = useState( false );

    const { data, loading, error } = useQuery( LOCALES_QUERY );

    const supportedLanguages = useMemo( () => data?.locales.edges.map( e => e.node ) ?? [], [ data ] );

    const [ currentLocale, setCurrentLocale ] = useState( translationService.getLocale() );

    useEffect( () => {
        const fn = loc => setCurrentLocale( loc );
        translationService.follow( fn );

        return () => {
            translationService.unfollow( fn );
        };
    }, [] );

    useEffect( () => {
        if ( preferredLanguageData ) {
            translationService.updateLocale( preferredLanguageData.preferredLanguage.value );
        }
    }, [ preferredLanguageData ] );

    /**
     * Triggers the update of the site to display content in a new language
     * specified by locale code.
     * 
     * @param localeCode - The locale code for the desired language.
     */
    const updateSiteLanguage = async ( localeCode ) => {
        // Set in a loading state.
        setLoading( true );

        translationService.updateLocale( localeCode );
        patchLanguage( {
            variables: {
                input: {
                    value: localeCode
                }
            }
        } );

        // Attempt to fetch translations for the requested language using the
        // translation service. If the language bundle is successfully retrieved,
        // it is stored in the intl-tools store and should automatically update
        // the site.
        const success = await translationService.fetchTranslations( localeCode );
        if ( !success ) {
            console.error( `Failed to use language '${ currentLocale }'.` );
        }

        // End the loading state.
        setLoading( false );
    };

    const getStringCb = useMemo( () => {
        return ( key, replace ) => getString( key, replace );
    }, [ currentLocale ] );

    const direction = useMemo( () => {
        return supportedLanguages.find( l => l.code == currentLocale )?.writingDirection;
    }, [ currentLocale ] );

    useEffect( () => {
        const elem = document.getElementsByTagName( 'html' )?.[ 0 ];

        if ( direction && elem ) {
            elem.dir = direction;
        }

    }, [ direction ] );


    return useMemo( () => ( {
        getString: getStringCb,
        keyExists,
        locale: currentLocale,
        setLocale: updateSiteLanguage,
        supportedLanguages,
        loading: isLoading || loading,
        userLocale: claims.locale,
        direction,
        error
    } ), [ updateSiteLanguage, isLoading, loading, supportedLanguages, error, currentLocale, direction ] );
};
